
import { useState, useEffect, useCallback, useRef } from 'react';
import { APIEndpoint } from '../utils/axios';
import { storageUtil } from '../utils/commonFunctions';

const DISMISS_DURATION_MS_ENV = Number(process.env.REACT_APP_ACCOUNT_DISMISS_WARNING_TIME);
const DISMISS_DURATION_MS = Number.isFinite(DISMISS_DURATION_MS_ENV) ? DISMISS_DURATION_MS_ENV : 24 * 60 * 60 * 1000; // 24 hours recheck
const POLL_INTERVAL_MS_ENV = Number(process.env.REACT_APP_ACCOUNT_LOGOUT_IDLE_TIME);
const POLL_INTERVAL_MS = Number.isFinite(POLL_INTERVAL_MS_ENV) ? POLL_INTERVAL_MS_ENV : 60 * 60 * 1000;


export function usePaymentWarning(sessionId) {
  const [warning, setWarning] = useState('');
  const [dueDate, setDueDate] = useState(null);
  const [isEnabled, setIsEnabled] = useState(false);
  const controllerRef = useRef(null);

  // Fetch stepAPI 
  const fetchPaymentInfo = useCallback(async (signal) => {
    if (!sessionId) return;
    
    try {
      const resp = await APIEndpoint.post(
        'customer/step-info',
        { id: sessionId, process: 'profile' },
        { signal }
      );

      const { paymentOverdue } = resp ?? {};

      setIsEnabled( (paymentOverdue && paymentOverdue?.enabled) ?? false);
      setWarning( (paymentOverdue && paymentOverdue?.message) ?? '');
      setDueDate( (paymentOverdue && paymentOverdue?.dueDate) ?? null);

    } catch (err) {
      if (err?.name !== 'AbortError') {
        console.warn('[usePaymentWarning] fetch failed:', err);
      }
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      setIsEnabled(false);
      setWarning('');
      setDueDate(null);
      return;
    }

    controllerRef.current = new AbortController();
    const dismissedKey = `notification_dismissed_${sessionId}`;
    const dismissedTime = storageUtil.get(dismissedKey, 'local');

    if (dismissedTime) {
      const timeSinceDismissed = Date.now() - parseInt(dismissedTime);
      const isExpired = timeSinceDismissed > DISMISS_DURATION_MS;

      if (!isExpired) {
        console.log('Log: warning is still dismissed, enabling warning');
        setIsEnabled(true);
        return;
      } else {
        console.log('Log: dismissal expired, clearing dismissed time');
        storageUtil.clear(dismissedKey, 'local');
      }
    }

    fetchPaymentInfo(controllerRef.current.signal);

    const pollTimer = setInterval(() => {
      const dismissedTime = storageUtil.get(dismissedKey, 'local');     
      if (!dismissedTime) {    
        return;
      }

      const dismissedAt = Number(dismissedTime);
      const timeSinceDismissed = Number.isFinite(dismissedAt) ? Date.now() - dismissedAt : Infinity;
      const isExpired = timeSinceDismissed > DISMISS_DURATION_MS;

      if (isExpired) {
        storageUtil.clear(dismissedKey, 'local');
        setIsEnabled(false);
        fetchPaymentInfo(controllerRef.current.signal);
      }
    }, POLL_INTERVAL_MS);

    return () => {
      clearInterval(pollTimer);
      controllerRef.current?.abort();
    };
  }, [sessionId, fetchPaymentInfo]);

  const dismissWarning = useCallback(() => {
    const dismissedKey = `notification_dismissed_${sessionId}`;
    storageUtil.set(dismissedKey, Date.now().toString(), 'local');
    setIsEnabled(false);
  }, [sessionId]);

  return {
    warning,
    dueDate,
    dismissWarning,
    isEnabled,
  };
}
